/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { BlogConfigFragment, PageHeroesFragment } from "@cosmos/hygraph-schema";

import { HeroAlpha, HeroGlow, HeroArticleImage, HeroBravo } from ".";

/**
 * True when a hero will render the page's `<h1>` (it has heading/CTA content), as
 * opposed to a purely decorative hero — e.g. a `CosmosHeroGlow` with only a blurred
 * background image, which renders no heading at all (HeroGlow.tsx returns null when
 * there's no title/desc/CTA). Every hero renders its title as `h1` *only when it has
 * that content*, so this drives whether the first ref-section should be promoted to
 * the page h1: a decorative hero must NOT suppress that promotion, or the page ends
 * up with no h1 (the /download bug — a glow hero + section titles all h2).
 *
 * Deliberately errs toward `true`: any heading/CTA content → the hero owns the h1,
 * so it can never cause the first section to emit a *second* h1 (MULTIPLE_H1). It
 * only reports `false` for a hero with no title, desc, eyebrow, buttons, or logo —
 * i.e. genuinely decorative.
 */
export const heroOwnsHeading = (hero?: PageHeroesFragment | null): boolean => {
	if (!hero) return false;
	// Fields vary across the hero union; read them loosely (any present → owns the h1).
	const h = hero as {
		title?: { text?: string | null; html?: string | null } | null;
		desc?: string | null;
		description?: string | null;
		eyebrow?: string | null;
		buttons?: unknown[] | null;
		modButtons?: unknown[] | null;
		logoImage?: { url?: string | null } | null;
	};
	const hasText = (s?: string | null): boolean => Boolean(s && /\S/.test(s));
	return (
		hasText(h.title?.text) ||
		hasText(h.title?.html) ||
		hasText(h.desc) ||
		hasText(h.description) ||
		hasText(h.eyebrow) ||
		Boolean(h.buttons?.length) ||
		Boolean(h.modButtons?.length) ||
		Boolean(h.logoImage?.url)
	);
};

export const getHero = (
	hero?: PageHeroesFragment | null,
	blogConfig?: BlogConfigFragment | null,
): React.JSX.Element | null => {
	if (!hero) return null;

	switch (hero.__typename) {
		case "CosmosHeroAlpha": {
			return <HeroAlpha {...hero} />;
		}
		case "CosmosHeroGlow": {
			return <HeroGlow {...hero} />;
		}
		case "CosmosHeroArticleImage": {
			return <HeroArticleImage {...hero} blogConfig={blogConfig} />;
		}
		case "CosmosHeroBravo": {
			return <HeroBravo {...hero} />;
		}
		default:
			return null;
	}
};
